import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { HUB_CALLBACK_PATH } from 'services/hub-callback';
import { endSession, LoginLocationState, sessionMonitor } from 'services/session';

const SIGNED_OUT_PATHS = ['/login', '/forgot-password', HUB_CALLBACK_PATH];

/**
 * Performs the one sign-out when the session monitor judges the session dead
 * (only ever with enforcement on), here or in another tab, and re-checks a
 * stale session when the user comes back to the window.
 *
 * Mounted once inside the router and never unmounted, so `handling` is only a
 * re-entrancy lock: a later session's death must still be handled.
 */
const SessionExpirationGuard = () => {
  const history = useHistory<LoginLocationState>();
  const handling = useRef(false);

  useEffect(() => {
    const handle = () => {
      if (handling.current) {
        return;
      }
      handling.current = true;
      try {
        const loginLocation = endSession();
        if (!SIGNED_OUT_PATHS.includes(history.location.pathname)) {
          history.replace(loginLocation);
        }
      } finally {
        handling.current = false;
      }
    };

    // Catches the tab left open overnight, rather than waiting for a failed request.
    const recheck = () => sessionMonitor.verifyIfStale();
    const recheckWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        recheck();
      }
    };

    // Subscribe before checking, so a death in between is not missed.
    const unsubscribe = sessionMonitor.onSessionDead(handle);
    if (sessionMonitor.isSessionDead()) {
      handle();
    }
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheckWhenVisible);

    return () => {
      unsubscribe();
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheckWhenVisible);
    };
  }, []);

  return null;
};

export default SessionExpirationGuard;
